export const validateFeeStructure = (values: any) => {
    const errors: any = {};
    const components = values?.feeStructure?.components ?? {};

    // Set a nested component-field error (value is a translation key resolved by React Admin).
    const setError = (component: string, field: string, key: string) => {
        errors.feeStructure = errors.feeStructure || {};
        errors.feeStructure.components = errors.feeStructure.components || {};
        errors.feeStructure.components[component] = errors.feeStructure.components[component] || {};
        errors.feeStructure.components[component][field] = key;
    };

    // Idle fee (mirrors backend validateIdleFee).
    const idle = components.idleFee;
    if (idle?.enabled === true) {
        if (typeof idle.rate !== 'number' || !Number.isFinite(idle.rate) || idle.rate <= 0) {
            setError('idleFee', 'rate', 'resources.fee-templates.validation.idleFeeRatePositive');
        }
        if (
            typeof idle.graceMinutes !== 'number' ||
            !Number.isInteger(idle.graceMinutes) ||
            idle.graceMinutes < 0
        ) {
            setError('idleFee', 'graceMinutes', 'resources.fee-templates.validation.idleFeeGraceMinutesInteger');
        }
    }

    // Session fee (mirrors backend validateSessionFee, sub-plan 05.1).
    const session = components.sessionFee;
    if (session?.enabled === true) {
        if (typeof session.amount !== 'number' || !Number.isFinite(session.amount) || session.amount < 0) {
            setError('sessionFee', 'amount', 'resources.fee-templates.validation.sessionFeeAmountNonNegative');
        }
    }

    // Time fee (mirrors backend validateTimeFee, sub-plan 05.1) — windowed, per-unit.
    const time = components.timeFee;
    if (time?.enabled === true) {
        if (time.unit !== 'minute' && time.unit !== 'hour') {
            setError('timeFee', 'unit', 'resources.fee-templates.validation.timeFeeUnitRequired');
        }
        if (!Array.isArray(time.rates) || time.rates.length === 0) {
            setError('timeFee', 'rates', 'resources.fee-templates.validation.timeFeeRatesRequired');
        } else if (
            time.rates.some(
                (r: any) => typeof r?.rate !== 'number' || !Number.isFinite(r.rate) || r.rate <= 0,
            )
        ) {
            setError('timeFee', 'rates', 'resources.fee-templates.validation.timeFeeRatePositive');
        }
        if (
            time.freeMinutes != null &&
            (typeof time.freeMinutes !== 'number' || !Number.isInteger(time.freeMinutes) || time.freeMinutes < 0)
        ) {
            setError('timeFee', 'freeMinutes', 'resources.fee-templates.validation.timeFeeFreeMinutesInteger');
        }
        if (
            time.maxChargeMinutes != null &&
            (typeof time.maxChargeMinutes !== 'number' ||
                !Number.isFinite(time.maxChargeMinutes) ||
                time.maxChargeMinutes < 0)
        ) {
            setError('timeFee', 'maxChargeMinutes', 'resources.fee-templates.validation.timeFeeMaxChargeMinutesNonNegative');
        }
        if (
            time.noLimitPreAuthAmount != null &&
            (typeof time.noLimitPreAuthAmount !== 'number' ||
                !Number.isFinite(time.noLimitPreAuthAmount) ||
                time.noLimitPreAuthAmount < 0)
        ) {
            setError('timeFee', 'noLimitPreAuthAmount', 'resources.fee-templates.validation.timeFeePreAuthNonNegative');
        }
    }

    // Energy-XOR-Time (mirrors backend validateFeeCombination EXACTLY, sub-plan 05.2): a template
    // cannot charge BOTH an energy fee and a time fee (both meter consumption). PRESENCE-based for
    // energy, like the backend authority (isEnergyActive consults rate/rates, NOT the `enabled`
    // flag), so the frontend rejects EXACTLY what the backend rejects, including an energy block
    // that is enabled:false but still carries rates. The form (09.1) also disables the opposite
    // toggle and clears the other's rates proactively; this wired validator is the save-time gate.
    const energy = components.energyFee;
    const energyActive =
        !!energy &&
        ((typeof energy.rate === 'number' && Number.isFinite(energy.rate)) ||
            (Array.isArray(energy.rates) && energy.rates.length > 0));
    const timeActive =
        time?.enabled === true &&
        Array.isArray(time.rates) &&
        time.rates.some((r: any) => typeof r?.rate === 'number' && Number.isFinite(r.rate) && r.rate > 0);
    if (energyActive && timeActive) {
        setError('timeFee', 'enabled', 'resources.fee-templates.validation.energyTimeMutuallyExclusive');
    }

    return errors;
};

/**
 * Save-time finalization (React Admin `transform` on Create/Edit): a DISABLED consumption component
 * must not carry rates. Energy billing is PRESENCE-based (it reads `rates`, not the `enabled` flag),
 * so a template saved with energyFee.enabled === false but rates still present would silently bill
 * energy. The form deliberately PRESERVES rates while you toggle a component off and on (so you do
 * not lose your work), and this strips them only at submit. Applied to both energyFee and timeFee
 * for a clean stored structure.
 */
export const stripDisabledConsumptionRates = (data: any) => {
    const components = data?.feeStructure?.components;
    if (!components) return data;
    const next: any = { ...components };
    if (next.energyFee && next.energyFee.enabled !== true) {
        next.energyFee = { ...next.energyFee, rates: [] };
    }
    if (next.timeFee && next.timeFee.enabled !== true) {
        next.timeFee = { ...next.timeFee, rates: [] };
    }
    return { ...data, feeStructure: { ...data.feeStructure, components: next } };
};
